"use client";
import { useRef } from "react";

export function TiltCard({
  children,
  className = "",
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Rect-Cache: getBoundingClientRect() liest Layout — bei mousemove (>100Hz)
  // gegen laufende CSS-Animationen wäre das forced sync layout. Stattdessen
  // beim Enter einmalig cachen und auf Scroll/Resize invalidieren.
  const rectRef = useRef<DOMRect | null>(null);
  // rAF-Throttle: mousemove wird auf einen Frame zusammengefasst.
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<{ x: number; y: number } | null>(null);

  const supportsHover =
    typeof window !== "undefined" ? window.matchMedia("(hover: hover)").matches : true;

  const cacheRect = () => {
    const el = ref.current;
    if (!el) return;
    rectRef.current = el.getBoundingClientRect();
  };

  const onEnter = () => {
    if (!supportsHover) return;
    cacheRect();
  };

  const onMove = (e: React.MouseEvent) => {
    if (!supportsHover) return;
    const el = ref.current;
    if (!el) return;
    pendingRef.current = { x: e.clientX, y: e.clientY };
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const pending = pendingRef.current;
      const rect = rectRef.current;
      const node = ref.current;
      if (!pending || !rect || !node) return;
      const x = (pending.x - rect.left) / rect.width; // 0..1
      const y = (pending.y - rect.top) / rect.height;
      const ty = (y - 0.5) * -8; // rotateX: top=neg
      const tx = (x - 0.5) * 8; // rotateY: right=pos
      node.style.setProperty("--tx", tx.toFixed(2));
      node.style.setProperty("--ty", ty.toFixed(2));
      node.classList.add("is-tilted");
    });
  };

  const onLeave = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingRef.current = null;
    rectRef.current = null;
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--tx", "0");
    el.style.setProperty("--ty", "0");
    el.classList.remove("is-tilted");
  };

  return (
    <div
      ref={ref}
      className={`lp-tilt-card ${className}`}
      style={style}
      onMouseEnter={onEnter}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </div>
  );
}
